import React from "react";
// The five marks down the left of the day, as the design team drew them. Each
// one is a finished thing — its own disc, its own fill and its own glyph —
// rather than a glyph this file then builds a disc around, which is why there
// is no colour for any of them anywhere below.
import { ReactComponent as AwaitingMark } from "../../../assets/svg/my task/mark pending.svg";
import { ReactComponent as CancelledMark } from "../../../assets/svg/my task/cancel.svg";
import { ReactComponent as CompletedMark } from "../../../assets/svg/my task/completed.svg";
import { ReactComponent as CurrentMark } from "../../../assets/svg/my task/current.svg";
import { ReactComponent as UpcomingMark } from "../../../assets/svg/my task/upcomming.svg";
import { LocationIcon, VideoIcon } from "../../../components/ui";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import {
  appointmentTimeLoud,
  attendeeName,
  durationLabel,
  hasEnded,
} from "../../appointments/appointments";
import { agendaLabel } from "../../appointments/meetingAgenda";
import type { ClientAppointment } from "../../appointments/types/appointments.types";
import type { TaskState } from "../myTask";

/**
 * The pill each row wears, where it wears one.
 *
 * `Upcoming` has no entry and `Awaiting` has none either, for opposite
 * reasons. An appointment later today is the ordinary case on a day's list —
 * every row that is not something else is one — and a column of identical
 * "Upcoming" pills says nothing that the row's position in the day has not
 * already said. `Awaiting` is not a state so much as a question, and its cell
 * is the two answers instead, the way an unpaid invoice's is.
 */
const PILL_STYLES: Partial<Record<TaskState, string>> = {
  InProgress: "bg-[#FDF3E2] text-[#9A6B1F]",
  Next: "bg-[#EFF6FF] text-[#3B72D4]",
  Completed: "bg-[#E7F8EF] text-[#2F8E5B]",
  Cancelled: "bg-[#F1F3F5] text-[#62636C]",
};

const PILL_LABELS: Partial<Record<TaskState, string>> = {
  InProgress: "In progress",
  Next: "Next",
  Completed: "Completed",
  Cancelled: "Cancelled",
};

/** Whether the row's time has been and gone, however it went. */
const isBehind = (state: TaskState): boolean =>
  state === "Completed" || state === "Cancelled" || state === "Awaiting";

/**
 * The line between one mark and the next: green behind the day as it has been
 * lived, grey in front of it.
 *
 * Solid either way. It was dashed ahead of the current hour once, on the
 * reading that a dash is provisional — but a dashed rule at this weight breaks
 * into a column of ticks that reads as a broken line rather than a planned
 * one, and on a day where nothing has happened yet the whole spine of the
 * screen was made of them. The colour already says which half of the day a
 * stretch belongs to, and it says it without taking the line apart.
 *
 * Read off the clock rather than off the state beside it, and the difference
 * shows on exactly one row: an appointment at six this evening that has been
 * called off is `Cancelled` but has not happened yet, and drawing the thread
 * above it as lived would say the day had got further than it has.
 *
 * It belongs to the *gap* rather than to either row, which is why it is drawn
 * in halves — see `TaskTimelineRow`. Both halves of one gap are told the same
 * thing, or the line would change colour halfway down.
 */
export type Thread = "lived" | "ahead";

const THREAD_STYLES: Record<Thread, string> = {
  lived: "border-[#4CC38A]",
  ahead: "border-[#D7DEE3]",
};

const threadAfter = (appointment: ClientAppointment, now: Date): Thread =>
  hasEnded(appointment, now) ? "lived" : "ahead";

/**
 * The mark against the time, down the left of the day.
 *
 * Five drawings for six states, because `Next` and `Upcoming` are the same
 * thing to the eye — both are a slot that has not come round yet — and the
 * difference between them is said by the pill on the row, where it is a word
 * rather than a shade of a circle nobody is going to decode.
 */
const MARKS: Record<TaskState, React.FC<React.SVGProps<SVGSVGElement>>> = {
  InProgress: CurrentMark,
  Next: UpcomingMark,
  Upcoming: UpcomingMark,
  Awaiting: AwaitingMark,
  Completed: CompletedMark,
  Cancelled: CancelledMark,
};

const TimelineMark: React.FC<{ state: TaskState }> = ({ state }) => {
  const Mark = MARKS[state];

  return (
    // A fixed 26px slot — the width of the largest mark, which is the current
    // one — with each mark centred inside it at whatever size it was drawn.
    //
    // They are deliberately not all one size: the design draws the hour that
    // is happening bigger than the ones behind it, and a settled tick smaller
    // still. Left to sit side by side they would each centre on a different
    // vertical and the thread running through them would zigzag by a few
    // pixels a row. The slot gives them one centre line to share. Nothing here
    // sets a size on the marks themselves for the same reason it sets no
    // colour: each file carries what it was drawn with.
    <span
      aria-hidden="true"
      className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center"
    >
      <Mark />
    </span>
  );
};

export interface TaskTimelineRowProps {
  appointment: ClientAppointment;
  state: TaskState;
  /**
   * The half-threads this row draws, or `null` where there is no neighbour on
   * that side: the first row has nothing above it and the last nothing below.
   *
   * Two halves rather than one line per row because the thread has to pass
   * through the middle of every mark, and a row cannot know how tall its
   * neighbour is. Each row draws from its own mark out to its own edge; the
   * halves meet in the gap between the cards and read as one line.
   */
  threadAbove: Thread | null;
  threadBelow: Thread | null;
  /** Off, with its reason, while the workspace has no plan behind it. */
  gate: GateProps;
  /** Opens the Meeting dialog on this appointment. */
  onOpen: (appointment: ClientAppointment) => void;
  /** The tick: the session took place. */
  onComplete: (appointment: ClientAppointment) => void;
  /** The cross: it did not. */
  onCancel: (appointment: ClientAppointment) => void;
}

const TaskTimelineRow: React.FC<TaskTimelineRowProps> = ({
  appointment,
  state,
  threadAbove,
  threadBelow,
  gate,
  onOpen,
  onComplete,
  onCancel,
}) => {
  const current = state === "InProgress";
  const behind = isBehind(state);
  const cancelled = state === "Cancelled";
  const pill = PILL_LABELS[state];
  // A way in is only worth offering while there is something to go in to. An
  // appointment that is over, or that was called off, has nothing to join —
  // and a link on one invites somebody to sit in an empty room.
  const joinable =
    !behind && appointment.type === "Online" && !!appointment.meetingUrl;
  const TypeIcon = appointment.type === "Online" ? VideoIcon : LocationIcon;
  // Whoever the hour is with — a client or an enquiry. Read through the one
  // helper rather than off `clientName`, which is `null` on every row booked
  // against an enquiry.
  const name = attendeeName(appointment) || "Unnamed client";
  // What the hour is for. `null` where none was chosen, which drops it and its
  // separator from the line rather than leaving a dot with nothing after it.
  const agenda = agendaLabel(appointment.agenda);

  return (
    // No bottom padding: the rows are spaced by the list's own `gap`, so a
    // row's box is exactly its card. That is what lets the mark sit at 50% and
    // land in the middle of the card rather than in the middle of the card
    // plus whatever air was underneath it.
    <li className="relative flex items-stretch gap-2">
      {/* The thread, in halves — up from the mark to this row's top edge, and
          down from the mark to its bottom. The `-top-4`/`-bottom-4` reach back
          across the list's gap, so this row's lower half meets the next row's
          upper half exactly and the line reads as one unbroken run. Drawn
          behind the marks, which carry their own fill and cover it — and at
          `left-[12px]`, which is the middle of `MARK_SLOT` and so the centre
          of every mark whatever size it was drawn at.

          `border-l-2` is solid by default, which is what this wants — see
          `THREAD_STYLES`, which now only picks the colour. */}
      {threadAbove && (
        <span
          aria-hidden="true"
          className={`absolute left-[12px] -top-4 bottom-1/2 w-0 border-l-2 ${THREAD_STYLES[threadAbove]}`}
        />
      )}
      {threadBelow && (
        <span
          aria-hidden="true"
          className={`absolute left-[12px] top-1/2 -bottom-4 w-0 border-l-2 ${THREAD_STYLES[threadBelow]}`}
        />
      )}

      {/* Centred against the card, not pinned to its top: the mark is what the
          thread runs through, so it has to sit at the row's middle whatever
          height the card happens to be. */}
      <div className="relative z-[1] flex w-[104px] 3xl:w-[118px] flex-shrink-0 items-center gap-2.5">
        <TimelineMark state={state} />
        <span
          className={`whitespace-nowrap text-[13px] 3xl:text-[14px] ${
            current ? "font-semibold text-ink" : "text-ink-muted"
          }`}
        >
          {appointmentTimeLoud(appointment.startsAt)}
        </span>
      </div>

      {/* The card. The one happening now is ringed in violet, carries the only
          shadow on the screen, and runs the full width of the row; every other
          card stops short of it. The day stays in the order it will be lived
          in — what changes is which row is loudest.

          The width is done by insetting the others rather than by growing the
          current one, and that is not a stylistic preference. This list
          scrolls, and anything reaching past a row's own box is cut off at the
          scroller's edge: when the current card was the one that grew, the
          clipping took a bite out of the right-hand border of the single card
          the whole arrangement exists to pick out. Shrinking its neighbours
          reads identically and cannot overflow anything.

          Right side only. The marks and the times run down the left at a fixed
          width, so every card already starts on the same line — insetting
          that edge too would bend the one column the eye uses to read down. */}
      <article
        className={`flex min-w-0 flex-1 items-stretch overflow-hidden rounded-[6px] border border-[#E3C6FD] transition-shadow ${
          current
            ? "shadow-[0px_2px_14px_0px_#3B2A2421]"
            : "mr-8 3xl:mr-10"
        } ${behind ? "bg-[#FCFDFD]" : "bg-white"}`}
      >
        {/* Everything but the actions is one press, which opens the dialog.
            One button rather than a clickable card, so the row is reachable
            from a keyboard and announced as the one thing it does — and so the
            tick, the cross and the Join beside it are not buttons inside a
            button, which is not a thing a browser will render. */}
        <button
          type="button"
          onClick={() => onOpen(appointment)}
          className="flex min-w-0 flex-1 items-stretch text-left transition-colors hover:bg-[#F8FEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/40"
        >
          {/* What kind of thing this is — and only that. Where it happens
              used to sit under the word; it has moved to the line beside the
              duration, where it reads as another fact about the appointment
              rather than as half of its name.

              Violet, which is the one colour on this screen that is not the
              brand teal: it marks the row's kind rather than its state, and
              the states beside it are already using teal, green, amber and
              grey between them. A called-off row is the exception, and goes
              grey with the rest of itself — the kind of thing it was stopped
              mattering when it stopped happening. */}
          <span
            className={`flex w-[84px] 3xl:w-[96px] flex-shrink-0 items-center justify-center px-3 py-3 text-[13px] 3xl:text-[14px] font-medium ${
              cancelled
                ? "bg-[#F1F3F5] text-[#9AA4AE]"
                : "bg-[#F7F1FF] text-[#7C5CFC]"
            }`}
          >
            Meeting
          </span>

          <span className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-4 py-3">
            {/* The rule is for a called-off appointment and nothing else. An
                hour that has simply been and gone still happened, and striking
                a person's name through for it reads as the person being
                struck out rather than the hour — that one is said by the mark
                on the thread and the pill on the row, both of which are about
                the appointment. A cancellation is the one case where the
                sitting itself was struck out, so the name carries it. */}
            <span
              className={`truncate text-[14px] 3xl:text-[15px] font-medium ${
                cancelled
                  ? "text-ink-muted line-through"
                  : behind
                  ? "text-ink-muted"
                  : "text-ink"
              }`}
            >
              {name}
            </span>

            {/* How long it runs, what it is for, where it happens, and —
                where it was called off — why. One line of facts separated by
                dots, rather than the type sitting up in the strip as though it
                were part of what this row is called. Only the agenda and the
                reason can run long, so they are the two allowed to truncate. */}
            <span className="flex min-w-0 items-center gap-1.5 text-[12px] 3xl:text-[13px] text-ink-muted">
              <span className="flex-shrink-0">
                {durationLabel(appointment.durationMinutes)}
              </span>

              {/* Straight after the length, which is where the design puts it
                  and is also where it earns its place: a day read top to
                  bottom is a column of names and hours, and what each hour is
                  *for* is the one thing that tells two of them apart. Allowed
                  to truncate, unlike the two facts around it — "Initial
                  consultation" is long and a narrow card has a reason beside
                  it to fit as well. */}
              {agenda && (
                <>
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 text-ink-subtle"
                  >
                    •
                  </span>
                  <span className="truncate">{agenda}</span>
                </>
              )}

              <span aria-hidden="true" className="flex-shrink-0 text-ink-subtle">
                •
              </span>
              <span className="flex flex-shrink-0 items-center gap-1">
                <TypeIcon aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                {appointment.type}
              </span>

              {cancelled && appointment.cancelReason && (
                <>
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 text-ink-subtle"
                  >
                    •
                  </span>
                  <span className="truncate">{appointment.cancelReason}</span>
                </>
              )}
            </span>
          </span>
        </button>

        <div className="flex flex-shrink-0 items-center gap-2 px-3 py-3 3xl:px-4">
          {/* A row whose time is up with nobody saying what became of it is
              not a state to report so much as a question to answer, so it
              carries the two answers rather than a pill. The same pair the
              client's own table and the payment history wear. */}
          {state === "Awaiting" ? (
            <>
              <span className="sr-only">Waiting to be marked</span>
              <PaymentAnswer
                tone="confirm"
                label={`Mark the ${appointmentTimeLoud(
                  appointment.startsAt
                )} appointment with ${name} as completed`}
                gate={gate}
                onClick={() => onComplete(appointment)}
              />
              <PaymentAnswer
                tone="cancel"
                label={`Cancel the ${appointmentTimeLoud(
                  appointment.startsAt
                )} appointment with ${name} — the session did not take place`}
                gate={gate}
                onClick={() => onCancel(appointment)}
              />
            </>
          ) : (
            pill && (
              <span
                className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${PILL_STYLES[state]}`}
              >
                {pill}
              </span>
            )
          )}

          {/* A link and not a button: this goes somewhere, so it opens in a new
              tab on a middle click and is announced as the way out that it is.
              Filled on the appointment that is happening now and outlined on
              everything else — on a day with four calls in it, exactly one of
              them is the one to be in. */}
          {joinable && (
            <a
              href={appointment.meetingUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className={
                current
                  ? "inline-flex items-center justify-center rounded-[6px] bg-brand px-4 py-2 text-[13px] 3xl:text-[14px] font-medium text-white transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                  : "inline-flex items-center justify-center rounded-[6px] border border-[#E0E0E0] bg-white px-4 py-2 text-[13px] 3xl:text-[14px] font-medium text-ink transition-colors hover:border-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              }
            >
              Join
              <span className="sr-only">
                {" "}
                the {appointmentTimeLoud(appointment.startsAt)} appointment with{" "}
                {name}
              </span>
            </a>
          )}
        </div>
      </article>
    </li>
  );
};

export interface TaskTimelineProps {
  /** The day, soonest first. Already sorted — this draws it as given. */
  appointments: ClientAppointment[];
  /** Each row's state, by appointment id — worked out once by the page. */
  states: Map<string, TaskState>;
  /**
   * The page's own clock, so the thread and the marks cannot disagree about
   * how far through the day the practice is.
   */
  now: Date;
  gate: GateProps;
  onOpen: (appointment: ClientAppointment) => void;
  onComplete: (appointment: ClientAppointment) => void;
  onCancel: (appointment: ClientAppointment) => void;
}

/**
 * The day, read top to bottom: a thread down the left with a mark and a time
 * at every appointment, and the appointment itself beside it.
 *
 * In the order it will be lived in, always. A list that floated the current
 * appointment to the top would be a list whose order changed under somebody
 * every time an hour turned over, and a diary that reorders itself is one
 * nobody can keep their place in. What the current hour gets instead is the
 * eye: its mark is the only ringed one, its time is the only bold one, and its
 * card is lifted out of the column with the screen's one shadow on it.
 */
const TaskTimeline: React.FC<TaskTimelineProps> = ({
  appointments,
  states,
  now,
  gate,
  onOpen,
  onComplete,
  onCancel,
}) => (
  // The air between rows lives on the list rather than inside them — see the
  // note on `TaskTimelineRow`'s own `<li>`.
  <ol className="flex flex-col gap-4">
    {appointments.map((appointment, index) => (
      <TaskTimelineRow
        key={appointment.id}
        appointment={appointment}
        state={states.get(appointment.id) ?? "Upcoming"}
        // The gap above this row is the one below the row before it, so both
        // halves of it are told the same thing.
        threadAbove={
          index > 0 ? threadAfter(appointments[index - 1], now) : null
        }
        threadBelow={
          index < appointments.length - 1 ? threadAfter(appointment, now) : null
        }
        gate={gate}
        onOpen={onOpen}
        onComplete={onComplete}
        onCancel={onCancel}
      />
    ))}
  </ol>
);

export default TaskTimeline;
